import type {
  AlteracaoDeAssinatura,
  AlteracaoDePlano,
  AssinaturaCriada,
  AssinaturaEncerrada,
  AssinaturaResumida,
  CancelamentoDeFatura,
  DetalheDaFatura,
  EncerramentoDeAssinatura,
  EstornoDeRecebimento,
  FiltroDeFatura,
  FinanceiroDoAluno,
  Inadimplencia,
  ListaDePlanos,
  NovaAssinatura,
  NovoPlano,
  PaginaDeFaturas,
  PedidoDeReajuste,
  PlanoNaLista,
  ReajusteAplicado,
  ReajusteNaLista,
  Recebimento,
  ResultadoDaRotina,
  ResultadoDoRecebimento,
  ResumoDeCobrancas,
  SimulacaoDeReajuste,
} from '@arena/shared/schemas';
import { api } from './cliente.js';

/**
 * As chamadas de planos e cobranças — `#v-planos`, `#m-plano`,
 * `#m-pagamento`, `#m-reajuste`, a tabela de `#v-dash`, a aba Financeiro
 * da ficha e o drill-down de inadimplência.
 *
 * DOIS recursos da matriz, duas superfícies: `planosApi` fala com
 * "Planos e valores" (`/planos`), `cobrancasApi` com "Cobranças e
 * inadimplência" (`/cobrancas`). A tela usa as duas; o servidor decide
 * cada uma pela célula dela.
 *
 * `faturas()` recebe opções TODO opcionais — e não o tipo inferido de
 * `zConsultaDeFaturas` —, pelo mesmo motivo de `financeiroApi.extrato`: o
 * `.default()` do zod torna os campos obrigatórios no tipo de saída.
 *
 * Ver: Arena_backend/Docs/plano/12-planos-faturas.md
 */
export const planosApi = {
  listar(): Promise<ListaDePlanos> {
    return api<ListaDePlanos>('/planos');
  },

  criar(dados: NovoPlano): Promise<PlanoNaLista> {
    return api<PlanoNaLista>('/planos', { metodo: 'POST', corpo: dados });
  },

  alterar(id: string, dados: AlteracaoDePlano): Promise<PlanoNaLista> {
    return api<PlanoNaLista>(`/planos/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  /** Não grava nada — a nota de `#m-reajuste`. */
  simularReajuste(dados: PedidoDeReajuste): Promise<SimulacaoDeReajuste> {
    return api<SimulacaoDeReajuste>('/planos/reajustes/simulacao', {
      metodo: 'POST',
      corpo: dados,
    });
  },

  aplicarReajuste(dados: PedidoDeReajuste): Promise<ReajusteAplicado> {
    return api<ReajusteAplicado>('/planos/reajustes', { metodo: 'POST', corpo: dados });
  },

  reajustes(): Promise<ReajusteNaLista[]> {
    return api<ReajusteNaLista[]>('/planos/reajustes');
  },

  assinar(dados: NovaAssinatura): Promise<AssinaturaCriada> {
    return api<AssinaturaCriada>('/planos/assinaturas', { metodo: 'POST', corpo: dados });
  },

  alterarAssinatura(id: string, dados: AlteracaoDeAssinatura): Promise<AssinaturaResumida> {
    return api<AssinaturaResumida>(`/planos/assinaturas/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  encerrarAssinatura(
    id: string,
    dados: EncerramentoDeAssinatura,
  ): Promise<AssinaturaEncerrada> {
    return api<AssinaturaEncerrada>(
      `/planos/assinaturas/${id}/encerrar`,
      { metodo: 'POST', corpo: dados },
    );
  },
};

export const cobrancasApi = {
  /** Sem mês, o corrente — no fuso da arena. */
  resumo(mes?: string): Promise<ResumoDeCobrancas> {
    return api<ResumoDeCobrancas>(
      mes ? `/cobrancas/resumo?mes=${encodeURIComponent(mes)}` : '/cobrancas/resumo',
    );
  },

  faturas(
    consulta: { filtro?: FiltroDeFatura; mes?: string; cursor?: string; limite?: number } = {},
  ): Promise<PaginaDeFaturas> {
    return api<PaginaDeFaturas>(`/cobrancas/faturas${queryString(consulta)}`);
  },

  fatura(id: string): Promise<DetalheDaFatura> {
    return api<DetalheDaFatura>(`/cobrancas/faturas/${id}`);
  },

  receber(id: string, dados: Recebimento): Promise<ResultadoDoRecebimento> {
    return api<ResultadoDoRecebimento>(`/cobrancas/faturas/${id}/receber`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /** Desfazer o recebimento — o razão estorna na origem. */
  estornar(id: string, dados: EstornoDeRecebimento): Promise<DetalheDaFatura> {
    return api<DetalheDaFatura>(`/cobrancas/faturas/${id}/estornar`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  cancelar(id: string, dados: CancelamentoDeFatura): Promise<DetalheDaFatura> {
    return api<DetalheDaFatura>(`/cobrancas/faturas/${id}/cancelar`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  inadimplencia(): Promise<Inadimplencia> {
    return api<Inadimplencia>('/cobrancas/inadimplencia');
  },

  doAluno(alunoId: string): Promise<FinanceiroDoAluno> {
    return api<FinanceiroDoAluno>(`/cobrancas/alunos/${alunoId}`);
  },

  /** "Gerar agora": a rotina da madrugada desta arena, na hora. */
  rotina(): Promise<ResultadoDaRotina> {
    return api<ResultadoDaRotina>('/cobrancas/rotina', { metodo: 'POST' });
  },
};

function queryString(parametros: Record<string, string | number | undefined>): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor !== undefined) busca.set(chave, String(valor));
  }
  const texto = busca.toString();
  return texto ? `?${texto}` : '';
}
